/**
 *
 * Registration
 *
 */
import React from 'react';
import { useTheme } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { TextRegular14, TextSemibold20 } from 'styles/typography';
import { Button, Input, Row, Form, Checkbox, Col } from 'antd';
import { RouteConstants } from 'app/routes';
import { Link } from 'react-router-dom';
import {
  MailOutlined,
  PhoneOutlined,
  UnlockOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { IRegisterFormValues, registerFormEnum } from '../../slice/types';

import * as S from './Registration.styles';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthorizationSlice } from '../../slice';
import { selectRegisterLoading } from '../../slice/selectors';
import { isValidPhoneNumber } from 'libphonenumber-js';

interface IProps {}
const REGISTER_FORM_ID = 'registerForm';

export const Registration = (props: IProps) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { actions } = useAuthorizationSlice();

  const isFormLoading = useSelector(selectRegisterLoading);

  const [form] = Form.useForm();

  const onSubmit = ({
    email,
    password,
    name,
    lastName,
    phoneNr,
  }: IRegisterFormValues) => {
    if (isFormLoading) {
      return;
    }
    dispatch(
      actions.registerUser({
        email: email.trim(),
        password,
        name: name.trim(),
        lastName: lastName.trim(),
        phoneNr: phoneNr.trim(),
      }),
    );
  };

  const { primary } = useTheme();
  return (
    <S.RegistrationCard
      bordered={false}
      title={
        <TextSemibold20>{t('Auth.Register.Title', 'Register')}</TextSemibold20>
      }
      headStyle={{ borderBottom: 'none', textAlign: 'center' }}
    >
      <Form
        name="register"
        form={form}
        id={REGISTER_FORM_ID}
        onFinish={onSubmit}
      >
        <Row gutter={25}>
          <Col flex="50%">
            <Form.Item
              name={registerFormEnum.name}
              rules={[{ required: true, whitespace: true }]}
            >
              <Input
                placeholder={t('Auth.Register.name', 'Name')}
                size="large"
                prefix={<UserOutlined style={{ color: primary }} />}
              />
            </Form.Item>
          </Col>
          <Col flex="50%">
            <Form.Item
              name={registerFormEnum.lastName}
              rules={[{ required: true, whitespace: true }]}
            >
              <Input
                placeholder={t('Auth.Register.lastName', 'Last name')}
                size="large"
                prefix={<UserOutlined style={{ color: primary }} />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={25}>
          <Col flex="50%">
            <Form.Item
              name={registerFormEnum.email}
              rules={[
                {
                  required: true,
                  type: 'email',
                  message: t(
                    'Validation.ShouldBeValidEmail',
                    'Should be valid email',
                  ),
                },
              ]}
            >
              <Input
                type="email"
                placeholder={t('Auth.Register.Email', 'Email')}
                size="large"
                prefix={<MailOutlined style={{ color: primary }} />}
              />
            </Form.Item>
          </Col>
          <Col flex="50%">
            <Form.Item
              name={registerFormEnum.phoneNr}
              rules={[
                { required: true, whitespace: true },
                () => ({
                  validator(_, value: string) {
                    const isValid = isValidPhoneNumber(value);
                    if (!value.trim() || isValid) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        t(
                          'Validation.PhoneNumberInvalid',
                          'Phone number is invalid.',
                        ),
                      ),
                    );
                  },
                }),
              ]}
            >
              <Input
                type="tel"
                placeholder={t('Auth.Register.PhoneNr', 'Phone number')}
                size="large"
                prefix={<PhoneOutlined style={{ color: primary }} />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={25} wrap={false}>
          <Col flex="50%">
            <Form.Item
              name={registerFormEnum.password}
              rules={[
                { required: true },
                {
                  min: 8,
                  message: t('Validation.PasswordMinLengthError', {
                    defaultValue:
                      'Password must be at least {{length}} characters long',
                    length: 8,
                  }),
                },
                () => ({
                  validator(_, value: string) {
                    if (!value || /[!@#$%^&*]/.test(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        t(
                          'Validation.PasswordSpeciaCharError',
                          'Password must contain at least one of special characters (!@#$%^&*)',
                        ),
                      ),
                    );
                  },
                }),
                () => ({
                  validator(_, value: string) {
                    if (
                      !value ||
                      /^(?:(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*)$/.test(value)
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        t(
                          'Validation.PasswordMustContainNumberLowerCaseAndUpperCase',
                          'Password must contain at least one number, upper case and lower case',
                        ),
                      ),
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder={t('Auth.Register.Password', 'Password')}
                size="large"
                prefix={<UnlockOutlined style={{ color: primary }} />}
              />
            </Form.Item>
          </Col>
          <Col flex="50%">
            <Form.Item
              name={registerFormEnum.passwordRepeat}
              dependencies={[registerFormEnum.password]}
              rules={[
                {
                  required: true,
                },
                ({ getFieldValue }) => ({
                  validator(_, value: string) {
                    if (
                      !value ||
                      getFieldValue(registerFormEnum.password) === value
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        t(
                          'Auth.Register.PasswordsDoesntMatch',
                          'Passwords do not match',
                        ),
                      ),
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder={t(
                  'Auth.Register.RepeatPassword',
                  'Repeat password',
                )}
                size="large"
                prefix={<UnlockOutlined style={{ color: primary }} />}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Form.Item
            name={registerFormEnum.hiikoPrivacyPolicy}
            valuePropName="checked"
            rules={[
              {
                required: true,
                transform: value => value || undefined,
                type: 'boolean',
                message: t(
                  'Auth.Register.PleaseAgreeTermsAndConditions',
                  'Please agree the terms and conditions.',
                ),
              },
            ]}
          >
            <Checkbox>
              {t(
                'Auth.Register.hiikoPrivacyPolicy',
                'I agree to the terms of use of the Hiiko platform',
              )}
            </Checkbox>
          </Form.Item>
        </Row>

        <Row justify="center">
          <Form.Item
            name={registerFormEnum.gudvilaPrivacyPolicy}
            valuePropName="checked"
            rules={[
              {
                required: true,
                transform: value => value || undefined,
                type: 'boolean',
                message: t(
                  'Auth.Register.PleaseAgreeTermsAndConditions',
                  'Please agree the terms and conditions.',
                ),
              },
            ]}
          >
            <Checkbox>
              {t(
                'Auth.Register.gudvilaPrivacyPolicy',
                'I agree with the privacy policy of UAB TEST',
              )}
            </Checkbox>
          </Form.Item>
        </Row>
        <Row justify="center">
          <Form.Item>
            <S.SubmitButton
              block
              form={REGISTER_FORM_ID}
              type="primary"
              size="large"
              htmlType="submit"
              loading={isFormLoading}
            >
              {t('Auth.Register.Submit', 'Register')}
            </S.SubmitButton>
          </Form.Item>
        </Row>
      </Form>

      <Row justify="center" align="middle">
        <TextRegular14>
          {t('Auth.Register.DoYouHaveAccount', 'Do you have an account?')}
        </TextRegular14>
        <Link to={RouteConstants.auth.login}>
          <Button type="link">
            {t('Auth.Register.LoginHere', 'Login here.')}
          </Button>
        </Link>
      </Row>
    </S.RegistrationCard>
  );
};
